import { useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Spinner } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import styled from 'styled-components';

import { fetchUrl } from '@utils/api';
import { buildProjectUrl } from '@utils/projectPath';
import { DEFAULT_PROJECT_VIEW_MODE, IMAGE_MODE } from '@utils/constants';
import logo from '@assets/logo/logo_white.svg';

const ThumbnailLoader = styled.div`
	width: 100%;
	height: 100%;

	display: flex;
	align-items: center;
	justify-content: center;
	aspect-ratio: 1 / 1;
`;

const ThumbnailWrapper = styled(Link)`
	width: 100%;
	height: 100%;

	display: flex;
	align-items: center;
	justify-content: center;
	aspect-ratio: 1 / 1;
`;

const Thumbnail = styled(Card.Img)`
	object-fit: cover;
	width: 100%;
	height: 100%;

	&.fallback {
		background-color: #000;
		aspect-ratio: 1 / 1;
		padding-inline: 1rem;
		object-fit: contain;
	}
`;

const VideoPreview = styled.video`
	object-fit: cover;
	width: 100%;
	height: 100%;
`;

const MAX_LOOPS = 2;

const VideoThumbnail = ({ uuid, projectPath, ...props }) => {
	const linkPath = projectPath || `/video-projects/${uuid}`;
	const loopCount = useRef(0);
	const videoRef = useRef(null);

	const { data: previewData, isLoading: previewLoading } = useQuery({
		queryKey: ['project_preview', uuid],
		queryFn: () => fetchUrl(`filelink?key=projectdata/${uuid}/preview.webm`),
		enabled: !!uuid,
	});

	const { data: thumbData } = useQuery({
		queryKey: ['project_thumbnail', uuid],
		queryFn: () => fetchUrl(`filelink?key=projectdata/${uuid}/thumbnail.png`),
		enabled: !!uuid,
	});

	const handleEnded = useCallback(() => {
		loopCount.current += 1;
		if (loopCount.current < MAX_LOOPS) {
			videoRef.current?.play();
		}
	}, []);

	const handleMouseEnter = useCallback(() => {
		const video = videoRef.current;
		if (video && video.paused) {
			loopCount.current = 0;
			video.play();
		}
	}, []);

	const handleMouseLeave = useCallback(() => {
		const video = videoRef.current;
		if (video && !video.paused) {
			video.pause();
		}
	}, []);

	if (previewLoading) {
		return (
			<ThumbnailLoader {...props}>
				<Spinner animation="border" role="status">
					<span className="visually-hidden">Loading preview</span>
				</Spinner>
			</ThumbnailLoader>
		);
	}

	const previewSrc = previewData?.url;
	const posterSrc = thumbData?.url;

	if (!previewSrc) {
		// Fallback to static thumbnail or logo
		return (
			<ThumbnailWrapper to={linkPath} {...props}>
				<Thumbnail
					variant="top"
					src={posterSrc || logo}
					className={posterSrc ? '' : 'fallback'}
				/>
			</ThumbnailWrapper>
		);
	}

	return (
		<ThumbnailWrapper
			to={linkPath}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			{...props}>
			<VideoPreview
				ref={videoRef}
				src={previewSrc}
				poster={posterSrc}
				muted
				playsInline
				autoPlay
				onEnded={handleEnded}
			/>
		</ThumbnailWrapper>
	);
};

const ProjectThumbnail = ({
	uuid,
	projectPath,
	imageMode,
	mode = DEFAULT_PROJECT_VIEW_MODE,
	...props
}) => {
	if (imageMode === IMAGE_MODE.VIDEO) {
		return <VideoThumbnail uuid={uuid} projectPath={projectPath} {...props} />;
	}

	// projectPath when the caller has one, else the project in the given view mode
	const linkPath = projectPath || buildProjectUrl(uuid, mode);

	const { data, isLoading } = useQuery({
		queryKey: ['project_thumbnail', uuid],
		queryFn: () => fetchUrl(`filelink?key=${uuid}/odm_orthophoto/thumbnail.png`),
		enabled: !!uuid,
	});
	const src = data?.url;

	if (isLoading) {
		return (
			<ThumbnailLoader {...props}>
				<Spinner animation="border" role="status">
					<span className="visually-hidden">Loading image</span>
				</Spinner>
			</ThumbnailLoader>
		);
	}

	return (
		<ThumbnailWrapper to={linkPath} {...props}>
			<Thumbnail variant="top" src={src ? src : logo} className={src ? '' : 'fallback'} />
		</ThumbnailWrapper>
	);
};

export default ProjectThumbnail;
